/** Formatting helpers for es-EC: "$1.204,50", "07/09/2026". */

const money = new Intl.NumberFormat('es-EC', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatMoney(n: number | string | null | undefined): string {
  const v = typeof n === 'string' ? Number(n) : n;
  if (v == null || Number.isNaN(v)) return money.format(0);
  // Intl gives "$1.204,50" for es-EC/USD; normalise the "US$" variant some engines emit.
  return money.format(v).replace('US$', '$').replace(/ /g, '');
}

/** Unit prices: 2–6 decimals as stored, trailing zeros trimmed (never round a 6-decimal price to 2 on screen). */
export function formatPrice(n: number | string | null | undefined): string {
  const v = typeof n === 'string' ? Number(n) : n;
  if (v == null || Number.isNaN(v)) return formatMoney(0);
  const s = new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(v);
  return s.replace('US$', '$').replace(/ /g, '');
}

/** Plain number with 2–6 decimals, trailing zeros trimmed (RIDE cells). */
export function formatPricePlain(n: number): string {
  const s = n.toFixed(6).replace(/0+$/, '');
  const [i, d = ''] = s.split('.');
  return `${i}.${d.padEnd(2, '0')}`;
}

export function formatNumber(n: number, decimals = 2): string {
  return new Intl.NumberFormat('es-EC', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n);
}

export function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** dd/MM/yyyy — the format the SRI and the emit body use. */
export function formatDateSri(d: Date): string {
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Parse many server date shapes (ISO, dd/MM/yyyy, epoch) into a Date or null. */
export function parseDate(v: unknown): Date | null {
  if (v == null || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v === 'number') return new Date(v > 1e12 ? v : v * 1000);
  if (typeof v === 'string') {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2}))?/.exec(v);
    if (m) return new Date(+m[3], +m[2] - 1, +m[1], m[4] ? +m[4] : 0, m[5] ? +m[5] : 0);
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function formatDate(v: unknown): string {
  const d = parseDate(v);
  return d ? formatDateSri(d) : '—';
}

export function formatDateTime(v: unknown): string {
  const d = parseDate(v);
  return d ? `${formatDateSri(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}` : '—';
}

/** "2107202601179001…0010040000001231" style truncation for long keys. */
export function middleEllipsis(s: string, head = 16, tail = 16): string {
  if (!s || s.length <= head + tail + 1) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
}

/** Lowercase and strip accents, for search matching and sort keys ("PERÚ" → "peru"). */
export function foldDiacritics(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

/** Short localized month and day from an ISO timestamp, in local time: "22 sept" · "Sep 22". */
/**
 * Day and month, plus the year whenever the date is not in the current one.
 *
 * Every caller is a plan date — a renewal, an allowance reset, a scheduled tier change — and an
 * annual subscription renews a year out. "Se renueva el 16 oct" on 16 October reads as today;
 * the year is the whole difference between "already due" and "eleven months away". Dates inside
 * this year keep the short form, which is the common case and the reason it exists.
 */
export function formatDateShort(v: unknown, locale: string): string {
  const d = parseDate(v);
  if (!d) return '—';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  try {
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) }).format(d);
  } catch {
    return formatDateSri(d);
  }
}
